(() => {
  const root = document.querySelector('[data-sunik-cart-drawer]');
  if (!root || root.dataset.ready || !window.fetch || !window.Intl) return;
  let strings;
  try { strings = JSON.parse(root.querySelector('[data-sd-strings]').textContent); } catch { return; }
  root.dataset.ready = 'true';
  const panel = root.querySelector('.sd-cart__panel');
  const body = root.querySelector('[data-sd-body]');
  const itemsNode = root.querySelector('[data-sd-items]');
  const statusNode = root.querySelector('[data-sd-status]');
  const errorNode = root.querySelector('[data-sd-error]');
  const checkout = root.querySelector('[data-sd-checkout]');
  const closeButton = root.querySelector('button[data-sd-close]');
  let cart = null;
  let cartFresh = false;
  let cartPageDirty = false;
  let busy = false;
  let opener = null;
  let previousOverflow = '';
  let queue = Promise.resolve();
  let operationCount = 0;
  const isOpen = () => !root.hidden;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const money = (amount) => {
    const currency = cart?.currency || root.dataset.currency || 'EUR';
    return new Intl.NumberFormat(root.dataset.locale || 'de', { style: 'currency', currency }).format(Number(amount) / 100);
  };
  const showError = (message) => { errorNode.textContent = message; errorNode.hidden = false; };
  const setBusy = (value, message = '') => {
    busy = value;
    body.setAttribute('aria-busy', String(value));
    itemsNode.querySelectorAll('button,input').forEach((control) => { control.disabled = value || !cartFresh; });
    checkout.disabled = value || !cartFresh || !cart?.item_count;
    if (message) statusNode.textContent = message;
  };
  const hideUnconfirmedCart = () => {
    cartFresh = false;
    itemsNode.hidden = true;
    root.querySelector('[data-sd-empty]').hidden = true;
    root.querySelector('[data-sd-summary]').hidden = true;
    root.querySelector('[data-sd-checkout-form]').hidden = true;
  };
  const syncCartPage = () => {
    if (cartPageDirty && !isOpen() && document.body.classList.contains('template-cart')) window.location.assign(root.dataset.cartUrl);
  };
  const enqueue = (operation, message) => {
    operationCount += 1;
    setBusy(true, message);
    const next = queue.then(operation);
    queue = next.catch(() => {});
    return next.finally(() => { operationCount -= 1; setBusy(operationCount > 0); if (!operationCount) syncCartPage(); });
  };
  const request = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...options.headers }, ...options });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result || result.status >= 400) {
      const failure = new Error(typeof result?.description === 'string' ? result.description : typeof result?.message === 'string' ? result.message : '');
      failure.fromShopify = Boolean(result);
      throw failure;
    }
    return result;
  };
  const focusContext = () => {
    const active = document.activeElement;
    const row = active?.closest('[data-sd-key]');
    if (!row || !root.contains(active)) return null;
    return { key: row.dataset.sdKey, index: [...itemsNode.children].indexOf(row), control: active.dataset.sdControl };
  };
  const restoreItemFocus = (context) => {
    if (!context || !isOpen()) return;
    const rows = [...itemsNode.children];
    const row = rows.find((item) => item.dataset.sdKey === context.key) || rows[Math.min(context.index, rows.length - 1)];
    const target = row?.querySelector(`[data-sd-control="${context.control}"]`);
    (target || closeButton).focus({ preventScroll: true });
  };
  const render = (nextCart, context = focusContext()) => {
    if (!Array.isArray(nextCart.items)) throw new Error(strings.loadError);
    cart = nextCart;
    cartFresh = true;
    itemsNode.hidden = false;
    const fragment = document.createDocumentFragment();
    cart.items.forEach((item) => {
      const row = element('article', 'sd-item');
      row.dataset.sdKey = item.key;
      const imageLink = element('a', 'sd-item__image');
      imageLink.href = safeUrl(item.url) || root.dataset.cartUrl;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const imageUrl = safeUrl(item.featured_image?.url || item.image || '');
      if (imageUrl && (item.featured_image?.url || item.image)) {
        const image = element('img'); image.src = imageUrl; image.alt = ''; image.width = 160; image.height = 200; image.loading = 'lazy'; imageLink.append(image);
      }
      row.append(imageLink);
      const info = element('div', 'sd-item__info');
      const title = item.product_title || item.title;
      const titleLink = element('a', 'sd-item__title', title); titleLink.href = safeUrl(item.url) || root.dataset.cartUrl; info.append(titleLink);
      if (item.variant_title && item.variant_title !== 'Default Title') info.append(element('p', 'sd-item__variant', item.variant_title));
      if (item.selling_plan_allocation?.selling_plan?.name) info.append(element('p', 'sd-item__variant', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).forEach(([key, value]) => { if (!key.startsWith('_') && value) info.append(element('p', 'sd-item__property', `${key}: ${value}`)); });
      const price = element('div', 'sd-item__price');
      if (item.original_line_price > item.final_line_price) price.append(element('s', '', money(item.original_line_price)));
      price.append(element('span', '', money(item.final_line_price))); info.append(price);
      if (item.unit_price_measurement && item.unit_price != null) {
        const unit = item.unit_price_measurement;
        info.append(element('p', 'sd-item__unit', `${money(item.unit_price)} / ${unit.reference_value === 1 ? '' : unit.reference_value}${unit.reference_unit}`));
      }
      (item.line_level_discount_allocations || []).forEach((discount) => info.append(element('p', 'sd-item__discount', `${discount.discount_application.title} −${money(discount.amount)}`)));
      const actions = element('div', 'sd-item__actions');
      const quantity = element('div', 'sd-item__quantity');
      const minus = element('button', '', '−'); minus.type = 'button'; minus.dataset.sdControl = 'decrease'; minus.setAttribute('aria-label', `${strings.decrease}: ${title}`);
      const input = element('input'); input.type = 'number'; input.min = '0'; input.step = '1'; input.value = item.quantity; input.inputMode = 'numeric'; input.required = true; input.dataset.sdControl = 'quantity'; input.setAttribute('aria-label', `${strings.quantity}: ${title}`);
      const plus = element('button', '', '+'); plus.type = 'button'; plus.dataset.sdControl = 'increase'; plus.setAttribute('aria-label', `${strings.increase}: ${title}`);
      quantity.append(minus, input, plus);
      const remove = element('button', 'sd-item__remove', strings.remove); remove.type = 'button'; remove.dataset.sdControl = 'remove'; remove.setAttribute('aria-label', `${strings.remove}: ${title}`);
      actions.append(quantity, remove); info.append(actions); row.append(info); fragment.append(row);
    });
    itemsNode.replaceChildren(fragment);
    const empty = !cart.item_count;
    root.querySelector('[data-sd-empty]').hidden = !empty;
    root.querySelector('[data-sd-summary]').hidden = empty;
    root.querySelector('[data-sd-checkout-form]').hidden = empty;
    root.querySelector('[data-sd-count]').textContent = cart.item_count;
    root.querySelector('[data-sd-subtotal]').textContent = money(cart.items_subtotal_price);
    root.querySelector('[data-sd-total]').textContent = money(cart.total_price);
    const discounts = root.querySelector('[data-sd-discounts]'); discounts.replaceChildren();
    (cart.cart_level_discount_applications || []).forEach((discount) => { const row = element('div', 'sd-cart__discount-row'); row.append(element('span', '', discount.title), element('span', '', `−${money(discount.total_allocated_amount)}`)); discounts.append(row); });
    document.querySelectorAll('.sn-cart-link').forEach((link) => { const badge = link.querySelector('span'); if (badge) badge.textContent = cart.item_count; link.setAttribute('aria-label', `${strings.title} (${cart.item_count})`); });
    setBusy(operationCount > 1);
    restoreItemFocus(context);
    document.dispatchEvent(new CustomEvent('sunik:cart-updated', { detail: { cart } }));
  };
  const refresh = (added = false) => enqueue(async () => {
    errorNode.hidden = true;
    hideUnconfirmedCart();
    try { render(await request(root.dataset.cartJsonUrl)); statusNode.textContent = added ? strings.added : ''; }
    catch { showError(added ? strings.addedLoadError : strings.loadError); statusNode.textContent = ''; }
  }, added ? strings.added : strings.loading);
  const open = ({ opener: source, added = false } = {}) => {
    if (!isOpen()) {
      opener = source || document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      root.hidden = false;
      closeButton.focus({ preventScroll: true });
    }
    return refresh(added);
  };
  const close = () => {
    if (!isOpen()) return;
    root.hidden = true;
    document.body.style.overflow = previousOverflow;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    if (!operationCount) syncCartPage();
  };
  const change = (key, quantity) => {
    if (busy || !cartFresh) return;
    if (!Number.isSafeInteger(quantity) || quantity < 0) { showError(strings.invalidQuantity); render(cart); return; }
    const context = focusContext();
    enqueue(async () => {
      errorNode.hidden = true;
      cartFresh = false;
      cartPageDirty = true;
      try {
        render(await request(root.dataset.changeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) }), context);
        statusNode.textContent = strings.updated;
      } catch (failure) {
        showError(failure.fromShopify && failure.message ? failure.message : strings.changeError);
        try { render(await request(root.dataset.cartJsonUrl), context); } catch { hideUnconfirmedCart(); }
        statusNode.textContent = '';
      }
    }, strings.updating);
  };
  itemsNode.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-sd-control]');
    if (!button || busy) return;
    const row = button.closest('[data-sd-key]');
    const item = cart?.items.find((line) => line.key === row?.dataset.sdKey);
    if (!item) return;
    const quantity = button.dataset.sdControl === 'remove' ? 0 : Math.max(0, item.quantity + (button.dataset.sdControl === 'increase' ? 1 : -1));
    change(item.key, quantity);
  });
  itemsNode.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-sd-control="quantity"]');
    if (input) change(input.closest('[data-sd-key]').dataset.sdKey, input.value === '' ? NaN : Number(input.value));
  });
  root.querySelectorAll('[data-sd-close]').forEach((node) => node.addEventListener('click', close));
  panel.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!busy || !link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const destination = link.href;
    queue.then(() => window.location.assign(destination));
  });
  root.querySelector('[data-sd-checkout-form]').addEventListener('submit', (event) => { if (busy || !cartFresh || !cart?.item_count) event.preventDefault(); });
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('.sn-cart-link');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault(); open({ opener: link });
  });
  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')].filter((node) => node.getClientRects().length > 0);
    const first = focusable[0]; const last = focusable[focusable.length - 1];
    if (!first) { event.preventDefault(); panel.focus(); }
    else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  document.addEventListener('focusin', (event) => { if (isOpen() && !panel.contains(event.target)) closeButton.focus({ preventScroll: true }); });
  document.addEventListener('sunik:cart-added', (event) => { cartPageDirty = true; open({ opener: event.detail?.opener, added: true }); });
  window.SunikCart = { open, close, refresh };
})();
